import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', property_type: 'apartment', status: 'active', description: '', address: { street: '', city: '', state: '', zip: '', country: '' } });
  const navigate = useNavigate();

  const fetchData = () => api.get('/properties').then(r => setProperties(r.data)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/properties/${editing.id}`, form);
      } else {
        await api.post('/properties', form);
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ name: '', property_type: 'apartment', status: 'active', description: '', address: { street: '', city: '', state: '', zip: '', country: '' } });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleEdit = (prop) => {
    setEditing(prop);
    setForm({ name: prop.name, property_type: prop.property_type || 'apartment', status: prop.status || 'active', description: prop.description || '', address: prop.address || { street: '', city: '', state: '', zip: '', country: '' } });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this property?')) return;
    await api.delete(`/properties/${id}`);
    fetchData();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;

  return (
    <div data-testid="properties-page">
      <div className="page-header">
        <h1 className="page-title">Properties</h1>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditing(null); setForm({ name: '', property_type: 'apartment', status: 'active', description: '', address: { street: '', city: '', state: '', zip: '', country: '' } }); } }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-property-btn"><Plus className="w-4 h-4 mr-2" />Add Property</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit Property' : 'Add Property'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="property-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select value={form.property_type} onValueChange={v => setForm({ ...form, property_type: v })}>
                    <SelectTrigger data-testid="property-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="apartment">Apartment</SelectItem>
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="villa">Villa</SelectItem>
                      <SelectItem value="condo">Condo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="property-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Street</Label>
                <Input value={form.address.street} onChange={e => setForm({ ...form, address: { ...form.address, street: e.target.value } })} data-testid="property-street-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>City</Label><Input value={form.address.city} onChange={e => setForm({ ...form, address: { ...form.address, city: e.target.value } })} /></div>
                <div><Label>Country</Label><Input value={form.address.country} onChange={e => setForm({ ...form, address: { ...form.address, country: e.target.value } })} /></div>
              </div>
              <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="property-submit-btn">{editing ? 'Update' : 'Create'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="table-wrap">
        <table className="w-full data-table min-w-[600px]">
          <thead><tr>
            <th>Name</th><th>Type</th><th>Location</th><th>Units</th><th>Status</th><th>Actions</th>
          </tr></thead>
          <tbody>
            {properties.map(p => (
              <tr key={p.id} className="cursor-pointer" onClick={() => navigate(`/properties/${p.id}`)} data-testid={`property-row-${p.id}`}>
                <td className="font-medium text-slate-900">{p.name}</td>
                <td className="capitalize">{p.property_type}</td>
                <td>{p.address?.city || 'N/A'}{p.address?.country ? `, ${p.address.country}` : ''}</td>
                <td>{p.unit_count || 0}</td>
                <td><Badge variant={p.status === 'active' ? 'default' : 'secondary'} className={p.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : ''}>{p.status}</Badge></td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(p)} data-testid={`edit-property-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-property-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {properties.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">No properties found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
